import { useState, useRef, useEffect } from "react";
import ClaudeService from "../../services/claudeService";
import { LUMEN_SYSTEM_PROMPT } from "../../services/prompts";
import TypewriterText from "../../components/TypewriterText";
import { useAutoResizeTextarea } from "../../hooks/useAutoResizeTextarea";
import { createPasteFallbackHandler } from "../../utils/pasteFallback";
import { formatDeskDocuments, formatDeskResults, chatHistoryForModel, stripReceiptEcho, isDeskReceiptSuccess, findDeskUpdate, softenPendingReply, matchDeskChoiceReply, describeDeskReceipt } from "../../lib/deskWrites";
import { withDeskItemRetry } from "../../services/lumenDeskRetry";
import { withCalendarRetry } from "../../services/lumenCalendarRetry";
import DeskReceiptLine from "./DeskReceiptLine";
import CalendarReceiptLine from "./CalendarReceiptLine";
import { describeMyDayNow } from "../../lib/dayPlanProposal";
import { toDateKey } from "../../lib/plannerItems";
import { describeCalendarForLumen, describeToday } from "../../lib/lumenCalendar";

const CHAT_INPUT_MAX_HEIGHT = 160;

export function FocusModeChat({ userProfile, activeSurface, whiteboardContents, projectOverview, surfaceState, onSurfaceUpdate, onDeskAction, roomName, buildingName, islandName, floorName, currentRoomActivity, onExit, onBrainDump, deskChoices = null }) {
  const [input, setInput]     = useState("");
  const [messages, setMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const prevMessagesLenRef = useRef(-1);
  useAutoResizeTextarea(inputRef, input, CHAT_INPUT_MAX_HEIGHT);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const isFirstRun = prevMessagesLenRef.current === -1;
    const grew = messages.length > prevMessagesLenRef.current;
    prevMessagesLenRef.current = messages.length;
    if (isFirstRun) {
      container.scrollTop = container.scrollHeight;
    } else if (grew || isThinking) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, [messages, isThinking]);

  const buildFocusPrompt = () => {
    const name = userProfile?.avatar?.name;
    const profileText = userProfile
      ? `Structure preference: ${userProfile.declared_structure_preference || "unknown"}\nRSD: ${userProfile.declared_rsd_sensitivity || "unknown"}\nUser name: ${name || "unknown"}`
      : "No profile.";
    return LUMEN_SYSTEM_PROMPT
      .replaceAll("{{ROOM_NAME}}", roomName || "My Room")
      .replaceAll("{{BUILDING_NAME}}", buildingName || "My Building")
      .replaceAll("{{ISLAND_NAME}}", islandName || "My Island")
      .replaceAll("{{FLOOR_NAME}}", floorName || "My Floor")
      .replaceAll("{{ACTIVE_SURFACE}}", activeSurface || "none")
      .replaceAll("{{CURRENT_MODE}}", "focus")
      .replaceAll("{{WHITEBOARD_CARDS}}", whiteboardContents || surfaceState?.whiteboardCards?.map(c => c.content).join(", ") || "empty")
      .replaceAll("{{BULLETIN_ITEMS}}", surfaceState?.bulletinItems?.map(c => c.content).join(", ") || "empty")
      .replaceAll("{{CALENDAR_EVENTS}}", describeCalendarForLumen(surfaceState?.calendarEvents, toDateKey()))
      .replaceAll("{{MY_DAY}}", describeMyDayNow(surfaceState?.plannerItems))
      .replaceAll("{{TODAY}}", describeToday())
      .replaceAll("{{DESK_DOCUMENTS}}", formatDeskDocuments(surfaceState?.deskDocuments))
      .replaceAll("{{DESK_RESULTS}}", formatDeskResults(messages))
      .replaceAll("{{USER_PROFILE}}", profileText)
      .replaceAll("{{SESSION_HISTORY}}", currentRoomActivity?.last_summary || "none")
      .replaceAll("{{HAS_WORLD}}", "yes") // FocusModeChat is only reachable from inside a room
      .replaceAll("{{PROJECT_OVERVIEW}}", projectOverview || "none");
  };
  // This chat keeps its own messages, so a desk choice made later is written back here.
  const settleDeskReceipt = (pendingId, receipt) => {
    setMessages(prev => prev.map(m => (m.deskReceipt?.pendingId === pendingId ? { ...m, deskReceipt: receipt } : m)));
  };

  const sendChatMessage = async(e) => {
    e.preventDefault();
    send();
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const send = async (text) => {
    const t = (text || input).trim();
    if (!t || isThinking) return;
    const userMsg = { role: "user", content: t };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    // Same as the main chat: a typed "update" / "keep both" / "skip" answers the open desk question.
    const typedChoice = deskChoices?.pending ? matchDeskChoiceReply(t, deskChoices.pending.kind) : null;
    if (typedChoice) {
      setIsThinking(true);
      try {
        const receipt = await deskChoices.resolve(typedChoice);
        if (receipt) setMessages(prev => [...prev, { role: "assistant", content: describeDeskReceipt(receipt) }]);
      } finally { setIsThinking(false); }
      return;
    }
    setIsThinking(true);
    try {
      // const res = await fetch("https://api.anthropic.com/v1/messages", {
      //   method: "POST", headers: API_HEADERS,
      //   body: JSON.stringify({
      //     model: "claude-sonnet-4-6", max_tokens: 2000,
      //     system: buildFocusPrompt(),
      //     messages: [...messages, userMsg].map(m => ({ role: m.role, content: m.content })),
      //   }),
      // });
      // const data = await res.json();
      // const raw = data.content?.[0]?.text || "{}";
      // const parsed = JSON.parse(raw.replace(/```json|```/g, "").trim());
      const callOptions = {
        model: "claude-sonnet-4-6",
        maxTokens: 2000,
        system: buildFocusPrompt(),
        messages: chatHistoryForModel([...messages, userMsg]),
      };

      let result = await ClaudeService.sendStructuredMessage(callOptions);

      if (!result.success && (result.status === 429 || result.status === 529)) {
        await new Promise(r => setTimeout(r, 2000));
        result = await ClaudeService.sendStructuredMessage(callOptions);
      }

      if (!result.success) {
        setMessages(prev => [...prev, { role: "assistant", content: "I had trouble reaching the server. Give me a moment and try again." }]);
        return;
      }

      const parsed = await withCalendarRetry(callOptions, await withDeskItemRetry(callOptions, result.data, { activeSurface }));
      const surfaceUpdates = parsed.surface_updates || [];
      const deskUpdate = findDeskUpdate(surfaceUpdates);
      const results = surfaceUpdates.filter(u => u !== deskUpdate).map(u => onSurfaceUpdate(u));
      const calendarReceipts = [
        ...results.map(r => r?.calendarReceipt).filter(Boolean),
        ...(parsed.calendarWriteUnsent
          ? [{ ...(parsed.calendarWriteUnsent === "my_day" ? { kind: "plan" } : {}), action: "failed", reason: "not_sent" }]
          : []),
      ];
      const calendarFailed = calendarReceipts.some(r => r.action === "failed");
      // Same rule as the main Lumen card: the reply waits for proof of what landed on the desk.
      let deskReceipt = null;
      if (deskUpdate || parsed.desk_action === "new_doc" || parsed.desk_action === "append" || parsed.deskWriteUnsent === true) {
        deskReceipt = onDeskAction
          ? await onDeskAction(parsed.desk_action || "new_doc", surfaceUpdates, { onPendingSettled: settleDeskReceipt })
          : { status: "failed", reason: "no_desk_update" };
      }
      const said = stripReceiptEcho(parsed.chat_response);
      let reply = (deskReceipt?.status === "pending" ? softenPendingReply(said) : said) || "...";
      if ((deskReceipt && !isDeskReceiptSuccess(deskReceipt)) || calendarFailed) {
        const base = { added: "add", put: "put", created: "create", saved: "save", placed: "place", sent: "send", updated: "update" };
        reply = reply.replace(/\bI've (added|put|created|saved|placed|sent|updated)\b/gi, (_, verb) => `I'll ${base[verb.toLowerCase()]}`);
      }
      setMessages(prev => [...prev, {
        role: "assistant",
        content: reply,
        ...(deskReceipt ? { deskReceipt } : {}),
        ...(calendarReceipts.length ? { calendarReceipts } : {}),
      }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "I had trouble reaching the server. Give me a moment and try again." }]);
    } finally { setIsThinking(false); }
  };

  const name = userProfile?.avatar?.name;

  return (
    <div style={{ width: "min(680px, 92vw)", height: "min(640px, 88vh)", display: "flex", flexDirection: "column", gap: 0 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(77,184,181,0.15)", border: "1px solid rgba(77,184,181,0.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#4DB8B5" }}>✦</div>
          <span style={{ fontSize: 15, fontWeight: 700, color: "white" }}>Lumen</span>
          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase" }}>· Focus</span>
        </div>
        <button onClick={onExit}
          style={{ padding: "6px 14px", borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.6)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.14)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}>
          ← Exit Focus
        </button>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, paddingRight: 4 }}>
        {messages.length === 0 && (
          <div style={{ alignSelf: "flex-start", maxWidth: "80%", background: "rgba(255,255,255,0.07)", borderRadius: "12px 12px 12px 4px", padding: "12px 16px", fontSize: 14, lineHeight: 1.7, color: "rgba(255,255,255,0.8)" }}>
            {name ? `Hey ${name}. Focus mode. Just you and me.` : "Focus mode. Just you and me."}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "80%", background: m.role === "user" ? "#4DB8B5" : "rgba(255,255,255,0.07)", color: m.role === "user" ? "white" : "rgba(255,255,255,0.85)", borderRadius: m.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px", padding: "10px 14px", fontSize: 14, lineHeight: 1.65, fontFamily: "'DM Sans',sans-serif", whiteSpace: "pre-wrap" }}>
            <TypewriterText text={m.content} animate={i === messages.length - 1 && m.role === "assistant" && !m.isRestored} />
            {m.deskReceipt && <DeskReceiptLine receipt={m.deskReceipt} onDark deskChoices={deskChoices} />}
            {m.calendarReceipts && <CalendarReceiptLine receipts={m.calendarReceipts} onDark />}
          </div>
        ))}
        {isThinking && <div style={{ alignSelf: "flex-start", background: "rgba(255,255,255,0.07)", borderRadius: "12px 12px 12px 4px", padding: "10px 16px", color: "#4DB8B5", fontSize: 18, letterSpacing: 3 }}>···</div>}
      </div>

      {/* Input */}
      <form onSubmit={sendChatMessage}
        style={{ display: "flex", gap: 10, marginTop: 14, alignItems: "flex-end" }}>
        <textarea
          ref={inputRef}
          value={input}
          rows={1}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
          onPaste={createPasteFallbackHandler(input, setInput)}
          placeholder="Tell Lumen what you need..."
          style={{ flex: 1, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: "12px 16px", color: "white", fontSize: 14, outline: "none", fontFamily: "'DM Sans',sans-serif", resize: "none", overflowY: "auto", maxHeight: CHAT_INPUT_MAX_HEIGHT, lineHeight: 1.5 }}
        />
        <button type="submit"
          disabled={!input.trim() || isThinking}
          style={{ padding: "12px 16px", borderRadius: 12, background: input.trim() && !isThinking ? "#4DB8B5" : "rgba(77,184,181,0.15)", border: "none", color: input.trim() && !isThinking ? "white" : "rgba(77,184,181,0.3)", fontSize: 18, fontWeight: 600, cursor: input.trim() ? "pointer" : "default", fontFamily: "'DM Sans',sans-serif", flexShrink: 0, lineHeight: 1 }}>
          ↑
        </button>
      </form>
      <div style={{ textAlign: "center", fontSize: 11, color: "rgba(255,255,255,0.2)", marginTop: 8 }}>Esc to exit</div>
    </div>
  );
}

export default FocusModeChat;
